// ─────────────────────────────────────────────────────────────────────────────
// Shared Message Template Library
//
// Used by:
//   • WhatsAppBulk.js          — bulk WhatsApp campaigns to parents
//   • Chat.js (Messages)       — in-app composer "📋 Templates" picker
//   • OperationsPanel.js       — admin UI to manage CUSTOM templates
//
// A template has the following shape:
//   {
//     id:          'unique_snake_case_id',
//     label:       'Emoji + Short Name',
//     category:    'announcement' | 'academic' | 'fees' | 'attendance' | 'operations' | 'urgent' | 'custom',
//     description: 'One-liner explaining when to use it',
//     perStudent:  true,  // recipient-specific placeholders ([Student Name], [Father Name], [Class], [Amount])
//                  false, // single body sent verbatim to everyone
//     filterClass: true,  // template's UI offers a class filter
//     autoSelect:  'all' | 'pending' | 'none',
//     extraFields: [ { key, label, type: 'text'|'textarea', placeholder } ],
//     preview:     (extra, schoolName) => 'rendered string with placeholders'
//   }
//
// Per-student placeholders that the WhatsApp backend understands today:
//   [Student Name]  [Father Name]  [Class]  [Amount]
// These get expanded per-recipient on the server. Keep using exactly these
// tokens so existing fan-out logic continues to work.
// ─────────────────────────────────────────────────────────────────────────────

export const TEMPLATE_CATEGORIES = [
  { key: 'announcement', label: '📢 Announcements' },
  { key: 'academic',     label: '📝 Academics' },
  { key: 'fees',         label: '💰 Fees' },
  { key: 'attendance',   label: '👤 Attendance' },
  { key: 'operations',   label: '🚌 Operations' },
  { key: 'urgent',       label: '🆘 Urgent' },
  { key: 'custom',       label: '⭐ My Templates' },
];

// ─── Starter (built-in) templates ─────────────────────────────────────────────
export const BUILT_IN_TEMPLATES = [
  // ── 1. Fee Reminder (per-student) ──────────────────────────────────────────
  {
    id:          'fee_reminder',
    label:       '💰 Fee Reminder',
    category:    'fees',
    description: 'Send pending fee reminders to selected parents',
    perStudent:  true,
    filterClass: true,
    autoSelect:  'pending',
    extraFields: [],
    preview: (extra, schoolName) =>
      `Hello [Father Name],\n\nThis is a gentle reminder that the school fee is pending for your child.\n\nStudent: [Student Name]\nClass: [Class]\nPending Amount: Rs.[Amount]\n\nKindly clear the dues at the earliest to avoid any inconvenience.\n\nThank you,\n${schoolName} School Management`,
  },

  // ── 2. Fee Payment Acknowledgement (per-student) ───────────────────────────
  {
    id:          'fee_acknowledgement',
    label:       '🧾 Fee Acknowledgement',
    category:    'fees',
    description: 'Thank parents after a fee payment is received',
    perStudent:  true,
    filterClass: true,
    autoSelect:  'none',
    extraFields: [
      { key: 'amount_paid',  label: 'Amount Paid (₹)',  type: 'text', placeholder: 'e.g. 5000' },
      { key: 'payment_date', label: 'Payment Date',     type: 'text', placeholder: 'e.g. 1st June 2026' },
      { key: 'receipt_no',   label: 'Receipt Number',   type: 'text', placeholder: 'e.g. RCT-2026-0431' },
    ],
    preview: (extra, schoolName) =>
      `Dear [Father Name],\n\nWe have received a fee payment of Rs.${extra.amount_paid || '[Amount Paid]'} for your child [Student Name] (Class [Class]) on ${extra.payment_date || '[Date]'}.\n\nReceipt No: ${extra.receipt_no || '[Receipt No]'}\n\nThank you for your prompt payment.\n\n${schoolName} School Management`,
  },

  // ── 3. General Announcement ────────────────────────────────────────────────
  {
    id:          'general_announcement',
    label:       '📢 General Announcement',
    category:    'announcement',
    description: 'Send any custom message to all selected parents',
    perStudent:  false,
    filterClass: true,
    autoSelect:  'all',
    extraFields: [
      { key: 'message', label: 'Your Message', type: 'textarea', placeholder: 'e.g. School will be closed tomorrow for elections. Stay safe.' },
    ],
    preview: (extra, schoolName) =>
      `Dear Parent,\n\n${extra.message || '[Your message here]'}\n\nThank you,\n${schoolName} School Management`,
  },

  // ── 4. Holiday Notice ──────────────────────────────────────────────────────
  {
    id:          'holiday_notice',
    label:       '🎉 Holiday Notice',
    category:    'announcement',
    description: 'Inform parents about school holidays',
    perStudent:  false,
    filterClass: false,
    autoSelect:  'all',
    extraFields: [
      { key: 'date',        label: 'Holiday Date',      type: 'text', placeholder: 'e.g. 1st May 2026' },
      { key: 'reason',      label: 'Reason',            type: 'text', placeholder: 'e.g. May Day' },
      { key: 'resume_date', label: 'Classes Resume On', type: 'text', placeholder: 'e.g. 2nd May 2026' },
    ],
    preview: (extra, schoolName) =>
      `Dear Parent,\n\nPlease be informed that the school will remain closed on ${extra.date || '[Date]'} due to ${extra.reason || '[Reason]'}.\n\nClasses will resume on ${extra.resume_date || '[Resume Date]'}.\n\nThank you,\n${schoolName} School Management`,
  },

  // ── 5. PTA / Parent Meeting ────────────────────────────────────────────────
  {
    id:          'ptm_meeting',
    label:       '👨‍👩‍👧 Parent Meeting',
    category:    'announcement',
    description: 'Invite parents to a PTA / parent–teacher meeting',
    perStudent:  false,
    filterClass: true,
    autoSelect:  'all',
    extraFields: [
      { key: 'date',   label: 'Meeting Date',  type: 'text', placeholder: 'e.g. 15th June 2026' },
      { key: 'time',   label: 'Time',          type: 'text', placeholder: 'e.g. 10:00 AM' },
      { key: 'venue',  label: 'Venue',         type: 'text', placeholder: 'e.g. School Auditorium' },
      { key: 'agenda', label: 'Agenda',        type: 'textarea', placeholder: 'e.g. Discuss half-yearly performance and upcoming activities.' },
    ],
    preview: (extra, schoolName) =>
      `Dear Parent,\n\nYou are cordially invited to a Parent–Teacher Meeting.\n\nDate: ${extra.date || '[Date]'}\nTime: ${extra.time || '[Time]'}\nVenue: ${extra.venue || '[Venue]'}\n\nAgenda:\n${extra.agenda || '[Agenda]'}\n\nYour presence is important. Kindly make it convenient to attend.\n\n${schoolName} School Management`,
  },

  // ── 6. Exam Notice ─────────────────────────────────────────────────────────
  {
    id:          'exam_notice',
    label:       '📝 Exam Notice',
    category:    'academic',
    description: 'Notify parents about upcoming exams',
    perStudent:  false,
    filterClass: true,
    autoSelect:  'all',
    extraFields: [
      { key: 'class_name', label: 'Class',           type: 'text', placeholder: 'e.g. Class 9' },
      { key: 'start_date', label: 'Exam Start Date', type: 'text', placeholder: 'e.g. 5th May 2026' },
    ],
    preview: (extra, schoolName) =>
      `Dear Parent,\n\nExaminations for Class ${extra.class_name || '[Class]'} are scheduled to begin from ${extra.start_date || '[Date]'}.\n\nKindly ensure your child is well prepared and present on time.\n\nThank you,\n${schoolName} School Management`,
  },

  // ── 7. Exam Timetable ──────────────────────────────────────────────────────
  {
    id:          'exam_timetable',
    label:       '📅 Exam Timetable',
    category:    'academic',
    description: 'Send subject-wise exam schedule',
    perStudent:  false,
    filterClass: true,
    autoSelect:  'all',
    extraFields: [
      { key: 'exam_name', label: 'Exam Name',     type: 'text',     placeholder: 'e.g. Half Yearly Exam' },
      { key: 'timing',    label: 'Daily Timing',  type: 'text',     placeholder: 'e.g. 9:30 AM – 12:30 PM' },
      { key: 'schedule',  label: 'Subject-wise Schedule', type: 'timetable-rows' },
    ],
    preview: (extra, schoolName) =>
      `Dear Parent,\n\nPlease find the timetable for ${extra.exam_name || '[Exam Name]'}.\n\nTiming: ${extra.timing || '[Timing]'}\n\nSchedule:\n${extra.schedule || '[Schedule]'}\n\nKindly ensure your child carries hall ticket, pen, and required stationery daily.\n\n${schoolName} School Management`,
  },

  // ── 8. Result Declaration ──────────────────────────────────────────────────
  {
    id:          'result_declaration',
    label:       '🏆 Result Declaration',
    category:    'academic',
    description: 'Announce that results are out and where to view',
    perStudent:  false,
    filterClass: true,
    autoSelect:  'all',
    extraFields: [
      { key: 'exam_name', label: 'Exam Name', type: 'text', placeholder: 'e.g. Half Yearly Exam' },
      { key: 'date',      label: 'Result Date', type: 'text', placeholder: 'e.g. 20th June 2026' },
      { key: 'where',     label: 'Where to View / Collect', type: 'text', placeholder: 'e.g. Available on the school app / class teacher' },
    ],
    preview: (extra, schoolName) =>
      `Dear Parent,\n\nResults for ${extra.exam_name || '[Exam Name]'} will be declared on ${extra.date || '[Date]'}.\n\n${extra.where || '[Where to View]'}\n\nFor any clarification, kindly meet the class teacher.\n\n${schoolName} School Management`,
  },

  // ── 9. Student Report (per-student) ────────────────────────────────────────
  {
    id:          'student_report',
    label:       '📊 Student Report',
    category:    'academic',
    description: 'Send marks, grade and attendance to parents',
    perStudent:  true,
    filterClass: true,
    autoSelect:  'all',
    extraFields: [
      { key: 'exam_name',  label: 'Exam Name',              type: 'text',     placeholder: 'e.g. Half Yearly Exam' },
      { key: 'subjects',   label: 'Subject-wise Marks',     type: 'textarea', placeholder: 'e.g. Telugu: 85/100 (A)\nHindi: 78/100 (B+)\nEnglish: 92/100 (A+)' },
      { key: 'grade',      label: 'Overall Grade',          type: 'text',     placeholder: 'e.g. A+' },
      { key: 'attendance', label: 'Attendance Percentage',  type: 'text',     placeholder: 'e.g. 92%' },
    ],
    preview: (extra, schoolName) =>
      `Hello [Father Name],\n\nPlease find below the academic report for your child.\n\nStudent: [Student Name]\nClass: [Class]\nExam: ${extra.exam_name || '[Exam Name]'}\n\nSubject-wise Marks:\n${extra.subjects || '[Subjects]'}\n\nOverall Grade: ${extra.grade || '[Grade]'}\nAttendance: ${extra.attendance || '[Attendance]'}\n\nFor details please visit the school app.\n\nThank you,\n${schoolName} School Management`,
  },

  // ── 10. Homework Reminder ──────────────────────────────────────────────────
  {
    id:          'homework_reminder',
    label:       '📚 Homework Reminder',
    category:    'academic',
    description: 'Remind parents about homework / project submission',
    perStudent:  false,
    filterClass: true,
    autoSelect:  'all',
    extraFields: [
      { key: 'subject',  label: 'Subject',      type: 'text',     placeholder: 'e.g. Maths' },
      { key: 'task',     label: 'Task',         type: 'textarea', placeholder: 'e.g. Complete exercise 5.2 (Q1–Q12) in classwork notebook.' },
      { key: 'due_date', label: 'Due Date',     type: 'text',     placeholder: 'e.g. 5th June 2026 (Monday)' },
    ],
    preview: (extra, schoolName) =>
      `Dear Parent,\n\nPlease ensure your child completes the following homework:\n\nSubject: ${extra.subject || '[Subject]'}\nTask: ${extra.task || '[Task]'}\nDue: ${extra.due_date || '[Due Date]'}\n\n${schoolName} School Management`,
  },

  // ── 11. Absent Today (per-student) ─────────────────────────────────────────
  {
    id:          'absent_today',
    label:       '🙋 Absent Today',
    category:    'attendance',
    description: 'Ask parent for reason when student is absent',
    perStudent:  true,
    filterClass: true,
    autoSelect:  'none',
    extraFields: [
      { key: 'date', label: 'Date', type: 'text', placeholder: 'e.g. 1st June 2026' },
    ],
    preview: (extra, schoolName) =>
      `Dear [Father Name],\n\nYour child [Student Name] (Class [Class]) was marked absent on ${extra.date || '[Date]'}.\n\nKindly inform the school about the reason for absence. If unwell, please share an update with the class teacher.\n\n${schoolName} School Management`,
  },

  // ── 12. Emergency Closure ──────────────────────────────────────────────────
  {
    id:          'emergency_closure',
    label:       '🚨 Emergency Closure',
    category:    'urgent',
    description: 'Urgent broadcast: school closed today / pick-up early',
    perStudent:  false,
    filterClass: false,
    autoSelect:  'all',
    extraFields: [
      { key: 'reason',     label: 'Reason',          type: 'text', placeholder: 'e.g. Heavy rain / Public unrest / Health advisory' },
      { key: 'action',     label: 'What Parents Should Do', type: 'textarea', placeholder: 'e.g. Kindly pick up your child by 11:00 AM. Transport will run from 10:45 AM.' },
    ],
    preview: (extra, schoolName) =>
      `🚨 URGENT NOTICE 🚨\n\nDear Parent,\n\nDue to ${extra.reason || '[Reason]'}, the school is being closed for the day.\n\n${extra.action || '[Action]'}\n\nYour child's safety is our top priority.\n\n${schoolName} School Management`,
  },
];

// Lookup helper
export const findTemplateById = (templates, id) =>
  templates.find((t) => t.id === id) || null;

// Build a synthetic template from a backend custom-template row so the rest of
// the UI can treat it like a built-in. Custom templates are simple: a category,
// a static body, and (optionally) the user inserts their own placeholders.
export const adaptCustomTemplate = (row) => ({
  id:          `custom_${row.template_id}`,
  template_id: row.template_id,          // server PK, used for edit/delete
  isCustom:    true,
  label:       `⭐ ${row.name}`,
  category:    row.category || 'custom',
  description: row.description || 'Custom template',
  perStudent:  false,
  filterClass: true,
  autoSelect:  'all',
  extraFields: [],
  body:        row.body || '',
  preview:     (_extra, _schoolName) => row.body || '',
});

// Render a template body (used by Chat composer "Insert" action).
export const renderTemplateBody = (template, extraParams = {}, schoolName = 'School') => {
  if (!template) return '';
  try { return template.preview(extraParams, schoolName) || ''; }
  catch { return template.body || ''; }
};
